import { FC } from 'react';
import Box from '@mui/material/Box/Box';
import Typography from '@mui/material/Typography/Typography';

import { colors } from 'styles';

import { IHeaderSection } from 'types/articles';

import SaveLinkIcon from 'assets/common/icons/save-link.svg';
import BlackLinkedInIcon from 'assets/common/icons/black-linkedin.svg';
import { useIsMobile } from 'hooks/useIsMobile';

export const HeaderSection: FC<IHeaderSection> = ({
  resourceType,
  author,
  image,
  imageCaption,
  minutesOfRead,
  publishDate,
  text,
  title,
  linkedInAuthorLink,
  authorImage,
  isCompany,
}) => {
  const isMobile = useIsMobile();

  // Shareable link to this article (canonical URL, without any hash/query).
  // Used as the anchor href so the browser shows a URL preview on hover, and
  // copied to the clipboard on click.
  const articleUrl =
    typeof window !== 'undefined'
      ? `${window.location.origin}${window.location.pathname}`
      : '';

  const handleCopy = async () => {
    if (!articleUrl) return;
    await navigator.clipboard.writeText(articleUrl);
  };

  return (
    <Box maxWidth="100%">
      <Typography
        color={colors.textPrimary}
        fontWeight={600}
        lineHeight="150%"
        marginBottom="16px"
        sx={{
          fontSize: '16px',
        }}
      >
        {resourceType}
      </Typography>

      <Typography
        component="h1"
        color={colors.textPrimary}
        fontWeight={700}
        lineHeight="120%"
        marginBottom="24px"
        sx={{
          fontSize: {
            md: 44,
            sm: 32,
            xxs: 24,
          },
        }}
      >
        {title}
      </Typography>

      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        paddingBottom="24px"
        sx={{
          borderBottom: '1px solid rgba(205, 205, 205, 1)',
        }}
      >
        {(resourceType === 'Article' || resourceType === 'White Paper' || resourceType === 'Technical Brief') && (
          <>
            <Box display="flex" alignItems="center">
              <Box
                marginRight="16px"
                sx={{
                  height: {
                    sm: '48px',
                    xxs: '32px',
                  },
                  width: isCompany
                    ? 'auto'
                    : {
                        sm: '48px',
                        xxs: '32px',
                      },
                  borderRadius: isCompany ? '8px' : '50%',
                  overflow: 'hidden',
                  flexShrink: 0,
                  cursor: 'pointer',
                  '& img': {
                    display: 'block',
                    width: isCompany ? 'auto' : '100%',
                    height: '100%',
                    objectFit: isCompany ? 'contain' : 'cover',
                    borderRadius: isCompany ? '8px' : '50%',
                  },
                }}
              >
                <img src={authorImage} alt="author" />
              </Box>

              <Box
                sx={{
                  color: colors.textPrimary,
                }}
              >
                <Typography
                  fontSize="14px"
                  fontWeight={600}
                  lineHeight="150%"
                  color={colors.textPrimary}
                  sx={{
                    textDecoration: 'none',
                  }}
                  component="a"
                  href={linkedInAuthorLink}
                  target="_blank"
                  rel="noreferrer"
                >
                  {author}
                </Typography>
                <Typography fontSize="14px" lineHeight="150%">
                  {publishDate} • {minutesOfRead} min read
                </Typography>
              </Box>
            </Box>

            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              sx={{
                flexDirection: {
                  xxs: 'column',
                  xs: 'row',
                },
              }}
            >
              <Box
                display="grid"
                gridTemplateColumns={{
                  xxs: 'repeat(2, 32px)',
                  xs: 'repeat(2, 1fr)',
                }}
                mt={{ xxs: '16px', xs: '0' }}
              >
                <Box>
                  <a
                    href={articleUrl}
                    onClick={(e) => {
                      e.preventDefault();
                      handleCopy();
                    }}
                    aria-label="Copy link to this article"
                  >
                    <img src={SaveLinkIcon} />
                  </a>
                </Box>

                <Box>
                  <a
                    href="https://www.linkedin.com/company/ailivesim"
                    target="_blank"
                    rel="noreferrer"
                  >
                    <img src={BlackLinkedInIcon} />
                  </a>
                </Box>
              </Box>
            </Box>
          </>
        )}
      </Box>

      {resourceType === 'White Paper' && image && (
        <Box
          sx={{
            maxWidth: '100%',
            maxHeight: '100%',
            width: '100%',
            cursor: 'pointer',
            marginTop: '25px',
            marginBottom: '48px',
          }}
        >
          <img
            src={image}
            alt="header image"
            style={{
              width: '100%',
              display: 'block',
            }}
          />
        </Box>
      )}

      <Box margin="48px 0">
        {typeof text === 'string' ? (
          <div
            style={{
              fontSize: isMobile ? '16px' : '18px',
              lineHeight: isMobile ? '150%' : '140%',
              color: colors.textPrimary,
            }}
          >
            {text}
          </div>
        ) : (
          <div
            style={{
              fontSize: isMobile ? '16px' : '18px',
              lineHeight: isMobile ? '150%' : '140%',
              color: colors.textPrimary,
            }}
          >
            {text}
          </div>
        )}
      </Box>

      {resourceType !== 'White Paper' && image && (
        <Box
          sx={{
            maxWidth: '100%',
            maxHeight: '100%',
            width: '100%',
            cursor: 'pointer',
          }}
        >
          <img
            src={image}
            alt="header image"
            style={{
              width: '100%',
              display: 'block',
            }}
          />
          {imageCaption && (
            <Typography
              sx={{
                marginTop: '8px',
                fontSize: '14px',
                lineHeight: '150%',
                color: colors.textSecondary,
              }}
            >
              {imageCaption}
            </Typography>
          )}
        </Box>
      )}
    </Box>
  );
};
